
import React, { useRef, useState, useMemo, useEffect } from 'react';
import { scaleLinear } from '@visx/scale';
import { Brush } from '@visx/brush';
import { PatternLines } from '@visx/pattern';
import { Group } from '@visx/group';
import { extent } from '@visx/vendor/d3-array';
import PlotGraph from './PlotGraph';
import MaskGraph from './MaskGraph';

// Initialize some variables

const brushMargin = { top: 10, bottom: 15, left: 65, right: 20 };
const chartSeparation = 30;
const PATTERN_ID = 'brush_pattern_';
const GRADIENT_ID = 'brush_gradient';

export const accentColor = '#f6acc8';
export const background = 'white';
export const background2 = 'black';

const selectedBrushStyle = {
  fill: `url(#${PATTERN_ID})`,
  stroke: 'white',
};

// 帯域幅
const BAND_RANGE = {
  min: 3400,
  max: 4100,
}

function GraphSetting({
  location,
  selectedMaskSetting,
  setSelectedMaskSetting,
  compact = false,
  traceData,
  width,
  height,
  margin = {
    top: 20,
    left: 65,
    bottom: 20,
    right: 20,
  },
  maskSetting,
  editMaskSetting,
  setEditMaskSetting,
  displaySettingJson,
  onMaskStatusChange,
  maxSettings,
}) {
  const [yRange, setYRange] = useState([-140, -95]);
  const brushRef = useRef(null);
  const [filteredData, setFilteredData] = useState(traceData);

  const onBrushChange = (domain) => {
    if (!domain) return;
    const { x0, x1 } = domain;

    const stockCopy = {
      "max": traceData.max.filter((s) => {
        const x = s.x;
        return x > x0 && x < x1;
      }),
      "20%": traceData["20%"].filter((s) => {
        const x = s.x;
        return x > x0 && x < x1;
      }),
      "ave": traceData.ave.filter((s) => {
        const x = s.x;
        return x > x0 && x < x1;
      }),
    }

    setFilteredData(stockCopy);
  };

  const innerHeight = height - margin.top - margin.bottom;
  const topChartBottomMargin = compact ? chartSeparation / 2 : chartSeparation + 20;
  const topChartHeight = 0.8 * innerHeight - topChartBottomMargin;
  const bottomChartHeight = innerHeight - topChartHeight - chartSeparation;

  // bounds
  const xMax = Math.max(width - margin.left - margin.right, 0);
  const yMax = Math.max(topChartHeight, 0);
  const xBrushMax = Math.max(width - brushMargin.left - brushMargin.right, 0);
  const yBrushMax = Math.max(bottomChartHeight - brushMargin.top - brushMargin.bottom, 0);

  // scales
  const xScale = useMemo(
    () =>
      scaleLinear({
        range: [0, xMax],
        domain: (() => {
          const result = extent(filteredData.max, ((d) => d.x))

          return [
            Math.max(result[0], BAND_RANGE.min),
            Math.min(result[1], BAND_RANGE.max),
          ]
        })(),
        nice: true,
      }),
    [filteredData, xMax],
  );

  const yScale = useMemo(
    () =>
      scaleLinear({
        range: [yMax, 0],
        domain: yRange,
        nice: true,
      }),
    [yMax, yRange],
  );
  const brushXScale = useMemo(
    () =>
      scaleLinear({
        domain: (() => {
          const result = extent(traceData.max, ((d) => d.x))

          return [
            Math.max(result[0], BAND_RANGE.min),
            Math.min(result[1], BAND_RANGE.max),
          ]
        })(),
        range: [0, xBrushMax],
      }),
    [xBrushMax, traceData.max],
  );
  const brushYScale = useMemo(
    () =>
      scaleLinear({
        range: [yBrushMax, 0],
        domain: yRange,
        nice: true,
      }),
    [yBrushMax, yRange],
  );

  const initialBrushPosition = useMemo(
    () => {
      return {
        start: { x: BAND_RANGE.min },
        end: { x: BAND_RANGE.max },
      }
    },
    [],
  );

  useEffect(() => {
    setFilteredData(traceData);
  }, [traceData]);

  useEffect(() => {
    if (brushRef.current) {
      brushRef.current.reset();
    }
  }, [location]);

  return (
    <div>
      <svg width={width} height={height}>
        <defs>
          <pattern id="brush_pattern_" width="8" height="8" patternUnits="userSpaceOnUse">
            <rect width="8" height="8" fill="rgba(255, 0, 0, 0.1)" /> {/* 背景色（薄赤） */}
            <path d="M0,0 L8,8" stroke="blue" strokeWidth="1" /> {/* ストライプ線 */}
          </pattern>
        </defs>

        <rect x={0} y={0} width={width} height={height} fill={`url(#${GRADIENT_ID})`} rx={14} />
        <PlotGraph
          id="plot"
          hideBottomAxis={compact}
          traceData={filteredData}
          width={width}
          margin={{ ...margin, bottom: topChartBottomMargin }}
          yMax={yMax}
          xScale={xScale}
          yScale={yScale}
          gradientColor={background2}
          displaySettingJson={displaySettingJson}
        >
          {/* マスク設定UI */}
          <MaskGraph
            location={location}
            selectedMaskSetting={selectedMaskSetting}
            setSelectedMaskSetting={setSelectedMaskSetting}
            xScale={xScale}
            xMax={xMax}
            yMax={yMax}
            margin={margin}
            xRange={BAND_RANGE}
            maskSetting={maskSetting}
            editMaskSetting={editMaskSetting}
            setEditMaskSetting={setEditMaskSetting}
            onMaskStatusChange={onMaskStatusChange}
            maxSettings={maxSettings}
          />
        </PlotGraph>

        {/* グラフの範囲選択 */}
        <PlotGraph
          id="brush"
          hideBottomAxis
          hideLeftAxis
          traceData={traceData}
          width={width}
          yMax={yBrushMax}
          xScale={brushXScale}
          yScale={brushYScale}
          margin={brushMargin}
          top={topChartHeight + topChartBottomMargin + margin.top}
          gradientColor={background2}
          displaySettingJson={displaySettingJson}
        >
          <PatternLines
            id={PATTERN_ID}
            height={8}
            width={8}
            stroke={accentColor}
            strokeWidth={1}
            orientation={['diagonal']}
          />
          {/* マスク設定UI */}
          <MaskGraph
            isReadOnly
            selectedMaskSetting={selectedMaskSetting}
            setSelectedMaskSetting={setSelectedMaskSetting}
            xScale={brushXScale}
            xMax={xBrushMax}
            yMax={yBrushMax}
            margin={brushMargin}
            xRange={BAND_RANGE}
            maskSetting={maskSetting}
            editMaskSetting={editMaskSetting}
            setEditMaskSetting={setEditMaskSetting}
            onMaskStatusChange={() => {}}
            maxSettings={maxSettings}
          />
          <Brush
            xScale={brushXScale}
            yScale={brushYScale}
            width={xBrushMax}
            height={yBrushMax}
            margin={brushMargin}
            handleSize={8}
            innerRef={brushRef}
            resizeTriggerAreas={['left', 'right']}
            brushDirection="horizontal"
            initialBrushPosition={initialBrushPosition}
            onChange={onBrushChange}
            onClick={() => setFilteredData(traceData)}
            selectedBoxStyle={selectedBrushStyle}
            useWindowMoveEvents
            renderBrushHandle={(props) => <BrushHandle {...props} />}
          />
        </PlotGraph>
      </svg>
    </div>
  );
}
// We need to manually offset the handles for them to be rendered at the right position
function BrushHandle({ x, height, isBrushActive }) {
  const pathWidth = 8;
  const pathHeight = 15;
  if (!isBrushActive) {
    return null;
  }
  return (
    <Group left={x + pathWidth / 2} top={(height - pathHeight) / 2}>
      <path
        fill="#f2f2f2"
        d="M -4.5 0.5 L 3.5 0.5 L 3.5 15.5 L -4.5 15.5 L -4.5 0.5 M -1.5 4 L -1.5 12 M 0.5 4 L 0.5 12"
        stroke="#999999"
        strokeWidth="1"
        style={{ cursor: 'ew-resize' }}
      />
    </Group>
  );
}

export default GraphSetting;
